/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Utility functions for profile data formatting and display
 */

/**
 * Accept only absolute HTTP(S) profile-picture URLs for browser rendering.
 * Invalid or credential-bearing values intentionally fall back to initials.
 */
export function normalizeProfilePictureUrl(value?: string | null): string {
  const normalized = value?.trim();
  if (!normalized) return '';

  try {
    const parsed = new URL(normalized);
    if (
      (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') ||
      parsed.username ||
      parsed.password
    ) {
      return '';
    }
    return normalized;
  } catch {
    return '';
  }
}

/**
 * Read the existing auth response shapes without inventing a second user model.
 * The central session endpoint returns a flat `picture`; older envelopes may
 * still provide `avatar`, `picture_url`, `user`, or `user_profile` fields.
 */
export function getProfilePictureValue(source: unknown): string {
  if (!source || typeof source !== 'object') return '';

  const record = source as Record<string, unknown>;
  const nestedUser =
    record.user && typeof record.user === 'object'
      ? (record.user as Record<string, unknown>)
      : undefined;
  const nestedProfile =
    record.user_profile && typeof record.user_profile === 'object'
      ? (record.user_profile as Record<string, unknown>)
      : undefined;

  for (const candidate of [
    record.picture,
    record.avatar,
    record.picture_url,
    nestedUser?.picture,
    nestedUser?.picture_url,
    nestedProfile?.picture,
    nestedProfile?.picture_url,
  ]) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return '';
}

/**
 * Format gender value for display
 */
export function formatGender(gender?: string): string {
  if (!gender) return 'Not specified';

  const genderMap: Record<string, string> = {
    male: 'Male',
    female: 'Female',
    other: 'Other',
    prefer_not_to_say: 'Prefer not to say',
  };

  return genderMap[gender.toLowerCase()] || gender;
}

/**
 * Format date of birth for display
 */
export function formatDateOfBirth(
  dateOfBirth?: string,
  locale: string = "en",
): string {
  if (!dateOfBirth) return 'Not specified';

  try {
    const date = new Date(dateOfBirth);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    console.warn('Invalid date format:', dateOfBirth);
    return 'Invalid date';
  }
}

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth?: string): number | null {
  if (!dateOfBirth) return null;

  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  } catch (error) {
    console.warn('Invalid date format for age calculation:', dateOfBirth);
    return null;
  }
}

/**
 * Format date of birth with age
 */
export function formatDateOfBirthWithAge(
  dateOfBirth?: string,
  locale: string = "en",
): string {
  const formattedDate = formatDateOfBirth(dateOfBirth, locale);

  if (formattedDate === 'Not specified' || formattedDate === 'Invalid date') {
    return formattedDate;
  }

  const age = calculateAge(dateOfBirth);
  if (age !== null) {
    return `${formattedDate} (${age} years old)`;
  }

  return formattedDate;
}

/**
 * Validate date of birth
 */
export function validateDateOfBirth(dateOfBirth: string): {
  isValid: boolean;
  error?: string;
} {
  if (!dateOfBirth) {
    return { isValid: true }; // Optional field
  }

  try {
    const date = new Date(dateOfBirth);
    const today = new Date();

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return { isValid: false, error: 'Invalid date format' };
    }

    // Check if date is not in the future
    if (date > today) {
      return { isValid: false, error: 'Date of birth cannot be in the future' };
    }

    // Check if age is reasonable (not older than 150 years)
    const age = calculateAge(dateOfBirth);
    if (age !== null && age > 150) {
      return { isValid: false, error: 'Date of birth seems unrealistic' };
    }

    return { isValid: true };
  } catch (error) {
    return { isValid: false, error: 'Invalid date format' };
  }
}

/**
 * Gender options for forms
 */
export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

/**
 * Get gender option by value
 */
export function getGenderOption(value?: string) {
  return GENDER_OPTIONS.find(option => option.value === value);
}

/**
 * Generate avatar initials from user profile
 * Priority: first_name + last_name -> email first letter -> fallback
 */
export function getAvatarInitials(
  userProfile?: {
    first_name?: string;
    last_name?: string;
    email?: string;
  },
  fallback: string = 'U'
): string {
  if (!userProfile) return fallback;

  const firstName = userProfile.first_name?.[0]?.toUpperCase() || '';
  const lastName = userProfile.last_name?.[0]?.toUpperCase() || '';

  // If we have first name or last name, use them
  if (firstName || lastName) {
    return firstName + lastName;
  }

  // Otherwise, use first letter of email
  return userProfile.email?.[0]?.toUpperCase() || fallback;
}

/**
 * Timezone options for forms - sorted by GMT offset
 */
export const TIMEZONE_OPTIONS = [
  // GMT-8
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (GMT-8)' },
  { value: 'America/Vancouver', label: 'America/Vancouver (GMT-8)' },

  // GMT-7
  { value: 'America/Denver', label: 'America/Denver (GMT-7)' },

  // GMT-6
  { value: 'America/Chicago', label: 'America/Chicago (GMT-6)' },
  { value: 'America/Mexico_City', label: 'America/Mexico City (GMT-6)' },

  // GMT-5
  { value: 'America/New_York', label: 'America/New York (GMT-5)' },
  { value: 'America/Toronto', label: 'America/Toronto (GMT-5)' },

  // GMT-3
  { value: 'America/Sao_Paulo', label: 'America/São Paulo (GMT-3)' },
  { value: 'America/Buenos_Aires', label: 'America/Buenos Aires (GMT-3)' },

  // GMT+0
  { value: 'UTC', label: 'UTC (GMT+0)' },
  { value: 'Europe/London', label: 'Europe/London (GMT+0)' },

  // GMT+1
  { value: 'Europe/Paris', label: 'Europe/Paris (GMT+1)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (GMT+1)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (GMT+1)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (GMT+1)' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (GMT+1)' },
  { value: 'Europe/Zurich', label: 'Europe/Zurich (GMT+1)' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm (GMT+1)' },
  { value: 'Africa/Lagos', label: 'Africa/Lagos (GMT+1)' },

  // GMT+2
  { value: 'Africa/Cairo', label: 'Africa/Cairo (GMT+2)' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (GMT+2)' },

  // GMT+3
  { value: 'Europe/Moscow', label: 'Europe/Moscow (GMT+3)' },

  // GMT+4
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GMT+4)' },

  // GMT+5:30
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (GMT+5:30)' },

  // GMT+7
  { value: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho Chi Minh (GMT+7)' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (GMT+7)' },
  { value: 'Asia/Jakarta', label: 'Asia/Jakarta (GMT+7)' },

  // GMT+8
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (GMT+8)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (GMT+8)' },
  { value: 'Asia/Manila', label: 'Asia/Manila (GMT+8)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala Lumpur (GMT+8)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong Kong (GMT+8)' },
  { value: 'Asia/Taipei', label: 'Asia/Taipei (GMT+8)' },
  { value: 'Australia/Perth', label: 'Australia/Perth (GMT+8)' },

  // GMT+9
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (GMT+9)' },
  { value: 'Asia/Seoul', label: 'Asia/Seoul (GMT+9)' },

  // GMT+10
  { value: 'Australia/Sydney', label: 'Australia/Sydney (GMT+10)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (GMT+10)' },

  // GMT+12
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (GMT+12)' },
] as const;

/**
 * Get timezone option by value
 */
export function getTimezoneOption(value?: string) {
  return TIMEZONE_OPTIONS.find(option => option.value === value);
}

/**
 * Get a consistent color based on the first letter of text
 * Useful for avatar backgrounds when no image is available
 */
export function getColorByFirstLetter(text: string): string {
  if (!text || typeof text !== 'string') {
    return '#415263'; // Default gray-600
  }

  // Get first letter and convert to uppercase
  const firstLetter = text.charAt(0).toUpperCase();

  // Map each letter A-Z to a color (using 600-level colors, allowing duplicates)
  const letterColorMap: Record<string, string> = {
    A: '#025eca', // blue-600
    B: '#288b03', // green-600
    C: '#d97f00', // orange-600
    D: '#bb2020', // red-600
    E: '#c79807', // yellow-600
    F: '#d59311', // gold-600
    G: '#8a7050', // brown-600
    H: '#2996a9', // teal-600
    I: '#10a5a5', // cyan-600
    J: '#88b80e', // lime-600
    K: '#d54718', // volcano-600
    L: '#7d2bc7', // purple-600
    M: '#c82880', // magenta-600
    N: '#4b49b6', // indigo-600
    O: '#025eca', // blue-600 (same as A)
    P: '#288b03', // green-600 (same as B)
    Q: '#d97f00', // orange-600 (same as C)
    R: '#bb2020', // red-600 (same as D)
    S: '#c79807', // yellow-600 (same as E)
    T: '#d59311', // gold-600 (same as F)
    U: '#8a7050', // brown-600 (same as G)
    V: '#2996a9', // teal-600 (same as H)
    W: '#10a5a5', // cyan-600 (same as I)
    X: '#88b80e', // lime-600 (same as J)
    Y: '#d54718', // volcano-600 (same as K)
    Z: '#7d2bc7', // purple-600 (same as L)
  };

  // Return mapped color or default if letter not found
  return letterColorMap[firstLetter] || '#415263'; // Default gray-600
}
